/* Handling of payment related state */
import {
  PAYMENT_SUBMIT,
  PAYMENT_CLEAR,
  UPDATE_SAVE_PAYMENT_METHOD,
  UPDATE_CURRENCY
} from '@stores/mutation-types';
import {
  PaymentMethod,
  Main as Donation
} from '@services/App/donation';
import {
  AMOUNT_MARKUP,
  PAYMENT_GATEWAY,
  PAYMENT_GATEWAY_TYPE,
  PAYMENT_GATEWAY_FEE,
  DONATION_FEE,
  DONATION_FIXED_FEES,
  FREQUENCY
} from '@constants';
import { MODULES } from '@constants';

import Forms from '@/form';

const state = () => ({

  // Payment Form
  form: new Forms({
    // Donation mission Id
    mission_id: null,

    // Donation amount
    amount: null,

    // Selected currency for donation
    currency: {
      code: 'EUR',
      sign: '€',
      exchange: {
        id: null,
        rate: null
      }
    },

    // If user/customer will cover the transaction fees.
    cover_fee: false,

    // Donation payment method id. if any
    payment_method_id: null,

    // Donation payment method used. if any
    payment_gateway_payment_method_id: null,

    // Donation billing info when no payment_method_id given.
    billing: {
      name: null,
      phone: null,
      address_line_1: null,
      address_line_2: null,
      city: null,
      state: null,
      country: null,
      postal_code: null
    },

    // Donation payment gateway used. default: STRIPE
    payment_gateway: PAYMENT_GATEWAY.STRIPE,

    // Donation payment method used. default: CARD
    payment_method_type: PAYMENT_GATEWAY_TYPE.CARD,

    frequency: {
      code: FREQUENCY.ONCE,
      personal_email: null
    },

    // Answers to donation questions
    answers: null,

    // Answer to static questions
    questions: {
      non_refundable: false,
      is_anonymous: false,
    }
  }),


  // Just for donation success modal after payment
  amount: null,

  // Payment settings
  settings: {
    savePaymentMethod: false,
    group_key: 'payment'
  },

  // Payment responses
  responses: {
    payment: null
  },

  // Failure count
  failures: 0
});

const mutations = {

  /**
   * @param {object} state stripe module states
   * @param {string} response Payment response
   *
   * @return void
   */
  [PAYMENT_SUBMIT]: (state, response) => {
    if (!response.success && response.recordFailure) {
      state.failures++;
    }

    state.amount = response.amount;
    state.responses.payment = response;
  },

  /**
   * Clears payment result state
   * @param {object} payment state
   * @param {bool} clear form
   *
   * @return void
   */
  [PAYMENT_CLEAR]: (state, clear = false) => {
    if (clear) {
      state.settings.savePaymentMethod = false;
      state.form.amount = null;
      state.form.cover_fee = false;
      state.form.payment_method_id = null;
      state.form.payment_gateway_payment_method_id = null;
      state.form.billing = {
        name: null,
        phone: null,
        address_line_1: null,
        address_line_2: null,
        city: null,
        state: null,
        country: null,
        postal_code: null
      };
      state.failures = 0;
      state.form.frequency = {
        code: FREQUENCY.ONCE,
        personal_email: null
      };
      return;
    }
    state.responses.payment = null;
  },

  [UPDATE_SAVE_PAYMENT_METHOD]: (state, isSavePaymentMethod) => {
    state.settings.savePaymentMethod = isSavePaymentMethod;
  },

  /**
   * Update currency data based on user
   *
   * @param {object} state stripe module states
   * @param {object} payload currency update
   *
   * @return void
   */
  [UPDATE_CURRENCY]: ({ form }, payload) => {
    form.currency = {
      code: payload.currency.code,
      sign: payload.currency.sign,
      exchange: {
        id: payload.id,
        rate: payload.rate
      }
    }
  }

};

const actions = {

  /**
   * Submit donation payment
   *
   * @param {object} context Vuex context keys
   *
   * @return {void}
   */
  async paymentSubmit({
    rootState: { customer },
    state,
    getters: { formData, totalAmount },
    dispatch,
    commit
  }) {
    const withoutPaymentMethod = customer.withoutPaymentMethod;

    if (withoutPaymentMethod && !formData.payment_method_id) {
      const {
        paymentMethod
      } = await dispatch('createPaymentMethod');

      formData.save_payment_method = state.settings.savePaymentMethod;
      formData.payment_gateway_payment_method_id = paymentMethod.id;

      // Check if amount is suspicious or not
      const verify = await dispatch('verifyAmount');

      if (!verify.success) {
        return await commit(PAYMENT_SUBMIT, {
          success: false,
          data: verify.data
        });
      }
    }

    const { success, data } = await Donation.createPaymentIntent(formData);

    if (!success) {
      return commit(PAYMENT_SUBMIT, {
        success,
        data
      });
    }

    if (formData.answers.length) {
      await Donation.createQuestionAnswers(
        formData.mission_id,
        data.id,
        formData.answers
      );
    }

    if (data.for_review) {
      return commit(PAYMENT_SUBMIT, {
        success,
        data,
        amount: totalAmount
      });
    }

    await dispatch('confirmPayment', {
      without_method: withoutPaymentMethod,
      save_method: formData.save_payment_method,
      client_secret: data.client_secret,
      payment_method_id: formData.payment_gateway_payment_method_id
    });
  },

  /**
   * Create payment method
   *
   * @param {object} context Vuex context keys
   *
   * @return {void}
   */
  async createPaymentMethod({ state, getters: { billingData }, dispatch }) {
    const result = await dispatch(`${MODULES.STRIPE}/stripeCreatePaymentMethod`, {
      group: state.settings.group_key,
      billing_details: billingData
    }, { root: true });

    if (result.error) {
      throw result.error; // Just throw a result error if Stripe gives an error
    };

    return result;
  },

  /**
   * Save payment method when setting savePaymentMethod is true
   *
   * @param {object} context Vuex context keys
   *
   * @return {void}
   */
  async savePaymentMethod(
    {
      rootState: { customer },
      getters: { billingData },
      dispatch
    },
    payload
  ) {
    await dispatch(`${MODULES.CUSTOMER}/storePaymentMethod`, {
      name: billingData.name,
      payment_gateway: PAYMENT_GATEWAY.STRIPE,
      payment_gateway_payment_method_id: payload.id,
      save_method_locally: payload.saveMethod
    }, { root: true });

    return customer.responses.serverSave;
  },

  /**
   * Save payment method when setting savePaymentMethod is true
   *
   * @param {object} context Vuex context keys
   *
   * @return {void}
   */
  async verifyAmount({ state, getters: { formData }, dispatch }) {
    const { success, data, response } = await Donation.verifyAmount({
      amount: formData.amount,
      currency: formData.currency
    });

    if (!success && response) return {
      success,
      data: response
    };

    if (!data.suspicious) return {
      success: true
    };

    const result = await dispatch('savePaymentMethod', {
      id: formData.payment_gateway_payment_method_id,
      saveMethod: formData.save_payment_method,
    });

    if (!result.success) return {
      success: false,
      data: result.data
    };

    return { success: true };
  },

  /**
   * Confirm payment
   *
   * @param {object} context Vuex context keys
   *
   * @return {void}
   */
  async confirmPayment({ state, getters: { isSubscription, totalAmount }, dispatch, commit}, payload) {
    const futureUsage = payload.without_method && (
      isSubscription
      || payload.save_method
    );

    const result = await dispatch(`${MODULES.STRIPE}/stripeConfirmPayment`, {
      client_secret: payload.client_secret,
      params: {
        payment_method: payload.payment_method_id,
        ...(futureUsage) && {
          setup_future_usage: 'off_session'
        }
      }
    }, {root: true});

    if (
      !result.error
      && payload.without_method
      && payload.save_method
    ) {
      await PaymentMethod.store({
        payment_gateway: PAYMENT_GATEWAY.STRIPE,
        payment_gateway_payment_method_id: payload.payment_method_id
      })
    }

    await commit(PAYMENT_SUBMIT, {
      success: result.error ? false : true,
      data: result.error || result.paymentIntent,
      amount: totalAmount,
      recordFailure: true
    });
  },

  /**
   * Update save payment method
   *
   * @param {object} context Vuex context keys
   * @param {bool} isSavePaymentMethod Id of the selected payment method
   *
   * @return {promise}
   */
  async updateSavePaymentMethod({ commit }, isSavePaymentMethod) {
    return commit(UPDATE_SAVE_PAYMENT_METHOD, isSavePaymentMethod);
  }
};

const getters = {

  /**
   * Object that will be used on payment endpoint
   *
   * @param {object} context Vuex context keys
   *
   * @return {object}
   */
  formData: ({ form, responses }) => {
    const data = form.data();

    data.exchange_rate_id = form.currency.exchange.id;
    data.cover_fee = data.cover_fee ? '1' : '0';
    data.currency = data.currency ? data.currency.code : null;

    return data;
  },

  /**
   * Billing object that needed on stripe.js method used
   *
   * @param {object} context Vuex context keys
   *
   * @return {object}
   */
  billingData: (state, { formData: { billing } }) => {
    if (!billing) {
      return null;
    }
    return {
      name: billing.name,
      address: {
        city: billing.city,
        country: billing.country,
        line1: billing.address_line_1,
        line2: billing.address_line_2,
        state: billing.state
      }
    }
  },

  /**
   * Calculate transaction fee for the donation
   *
   * @param {object} context Vuex context keys
   *
   * @return {object}
   */
  transactionFee: ({ form }) => {
    let fixedFee = 0;

    if (form.currency.code in DONATION_FIXED_FEES) {
      fixedFee = DONATION_FIXED_FEES[form.currency.code];
    } else {
      const rate = form.currency.exchange.rate;
      fixedFee = (parseFloat(rate) * (AMOUNT_MARKUP + 1)) * PAYMENT_GATEWAY_FEE;
    }

    const amount = parseFloat(form.amount || 0) + fixedFee;
    const charge = amount / (1 - DONATION_FEE)
    const fee = charge - form.amount;
    const factor = Math.pow(10, 2);
    const total = Math.ceil(fee * factor) / factor;

    return total.toFixed(2).replace('.00','');
  },

  /**
   * Total amount of the donation including fees without currency sign
   *
   * @param {object} context Vuex context keys
   *
   * @return {object}
   */
   totalAmountNumber: ({ form }, { transactionFee }) => {
    let amount = 0;
    if (form.cover_fee) {
      amount = parseFloat(form.amount) + parseFloat(transactionFee);
    } else {
      amount = parseFloat(form.amount || 0);
    }

    return form.amount && !isNaN(amount)
      ? parseFloat(Number(amount).toFixed(2))
      : null;
  },

  /**
   * Total amount of the donation including fees with currency sign
   *
   * @param {object} context Vuex context keys
   *
   * @return {object}
   */
  totalAmount: ({ form }, { totalAmountNumber }) => {
    const sign = form.currency.sign;

    return totalAmountNumber
      ? `${sign} ${totalAmountNumber}`
      : null;
  },

  /**
   * Check if payment have an error and get it
   *
   * @param {object} context Vuex context keys
   *
   * @return {object}
   */
  paymentError: ({ responses: { payment } }) => {
    if (payment && !payment.success) {
      // error from api is in array
      const error = payment.data[0] || payment.data;
      return {
        code: error.code,
        message: error.message
      }
    }

    return false;
  },

  /**
   * Check if payment gateway is stripe
   * @return {Boolean}
   */
  isPoweredByStripe: ({form : {payment_gateway}}) => {
    return payment_gateway === PAYMENT_GATEWAY.STRIPE;
  },

  /**
   * Return selected payment frequency
   * @return {String}
   */
  paymentFrequency: ({form: { frequency }}) => {
    return frequency.code || '';
  },

  /**
   * Check if form is subscription or not
   * @return {String}
   */
  isSubscription: ({form: { frequency }}) => {
    return frequency.code !== FREQUENCY.ONCE;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
